
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Api from "services/api"
import toast from "react-hot-toast";
import ConfirmationModal from "components/common/ConfirmationMessage";
import { ListLayout } from "components/common/ListLayout";
import { CustomSwitch } from "components/common/Switch";
import AddFacility from "components/tools/facility/AddFacility";
import { convertTimeToUTC } from "helpers/convertTimeToUTC";
import { usePermissions } from "layouts/Admin";
import { changeStatus, fetchFacilities, facilitiesSelector, removeMultipleFacilities } from "slices/facilitiesSlice";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";

function FacilityList() {
    const dispatch = useDispatch();
    const { checkPermission } = usePermissions();
    const { facilities, last_page: totalPages, loader, total, current_page, per_page: pageLength } = useSelector(facilitiesSelector);

    const [addForm, setAddForm] = React.useState({
        show: false,
        item: {}
    });
    const refAddutton = React.useRef(null);
    const [selectedItems, setSelectedItems] = React.useState([]);
    const [selectAll, setSelectAll] = React.useState(false);
    const [deleteModal, setDeleteModal] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [sortConfig, setSortConfig] = React.useState({
        sortBy: 'name',
        sortDirection: 'asc',
    });

    React.useEffect(() => {
        dispatch(fetchFacilities(1))
    }, [])

    const handleAddFacilityPopUp = (item) => {
        setAddForm({
            show: true,
            item,
        });
    };

    const hideConfirmDialog = () => {
        setDeleteModal(false);
    }

    const submitCallback = (status, message) => {
        if (status) {
            toast.success(message, {
                className: "successToast",
            })
            setSelectedItems([]);
            setSelectAll(false)
            dispatch(fetchFacilities(current_page, { search: searchTerm, sortBy: sortConfig.sortBy, sortDirection: sortConfig.sortDirection }))
            hideConfirmDialog()
        }
        else {
            toast.error(message, {
                className: "errorToast",
            });
        }
    };

    const handleRemoveItems = () => {
        dispatch(removeMultipleFacilities({ ids: selectedItems }, submitCallback))
    }

    const handleChange = (e) => {
        const id = e.target.dataset.value
        setSelectedItems(
            selectedItems.includes(id) ?
                selectedItems.filter((item) => item != id) :
                [...selectedItems, id]
        )
    };

    const handleToggleSelectAll = () => {
        if (selectAll) {
            setSelectedItems([])
            setSelectAll(false)
            return
        }
        setSelectAll(true)
        setSelectedItems(facilities.map((facility) => facility.id.toString()))
    }

    const renderTableHeader = React.useCallback(() => {
        const callBack = (dir, sortBy) => {
            setSortConfig({ sortBy, sortDirection: dir })
            dispatch(fetchFacilities(current_page, { sortBy: sortBy, sortDirection: dir, search: searchTerm }))
        }

        return (
            <tr>
                <th className="col-2">
                    <div className="row_check">
                        <span>#</span>
                        {checkPermission('Delete Facilities') &&
                            <label className="custom-checkbox checkbox"><input type="checkbox" name="select-all" checked={selectAll} onChange={handleToggleSelectAll} /><span className="checkmark"></span></label>
                        }
                    </div>
                </th>
                <th className="col-4">
                    <TableHeaderItem
                        title="Facility Name"
                        sortBy="name"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                <th className="col-3">
                    <TableHeaderItem
                        title="Creation Date"
                        sortBy="created_at"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                {checkPermission('Status Facilities') &&
                    <th className="col-3">Status</th>
                }
                {checkPermission('Edit Facilities') &&
                    <th className="col-3">Actions</th>
                }
            </tr>
        )
    }, [selectAll, searchTerm, sortConfig, current_page, selectedItems, facilities])

    const renderTableContent = React.useCallback(() => {
        return (
            facilities?.map((facility, index) => (
                <FacilityItem
                    key={facility.id}
                    facility={facility}
                    index={index}
                    selectedItems={selectedItems}
                    handleChange={handleChange}
                    handleAddPopUp={handleAddFacilityPopUp}
                    current_page={current_page}
                    pageLength={pageLength}
                    checkPermission={checkPermission}
                />
            ))
        )
    }, [facilities, selectedItems]);

    const handleClose = () => {
        refAddutton.current.click();
        setAddForm({ show: false })
    };

    const showConfirmDialog = () => {
        setDeleteModal(true);
    };

    return (
        <ListLayout
            heading="Facility"
            time={true}
            title=""
            buttonTitle="Add Facility"
            renderTableHeader={renderTableHeader}
            renderTableContent={renderTableContent}
            totalPages={totalPages}
            addButtonDisabled={!checkPermission('Create Facilities')}
            fetchList={fetchFacilities}
            selectedItems={selectedItems}
            handleRemoveItems={handleRemoveItems}
            renderAddForm={(callback) => <AddFacility item={addForm.item} show={addForm.show} onClose={handleClose} callback={callback} statusPerm={checkPermission('Status Facilities')} />}
            refAddutton={refAddutton}
            addForm={addForm}
            showConfirmDialog={showConfirmDialog}
            handleAddPopUp={handleAddFacilityPopUp}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            loader={loader}
            noOfItems={total}
            searchPlaceholder="Search facility ..."
        >
            {deleteModal && <ConfirmationModal onClose={hideConfirmDialog} isOpen={deleteModal} onConfirm={handleRemoveItems} />}
        </ListLayout>
    )
}

const FacilityItem = ({
    facility, index, selectedItems, handleChange, handleAddPopUp, current_page, pageLength, checkPermission
}) => {
    const dispatch = useDispatch();
    const [status, setStatus] = useState(facility?.status);

    React.useEffect(() => {
        setStatus(facility?.status || 0)
    }, [facility])

    const handleStatusChange = (id, s) => {
        setStatus(s ? 1 : 0)
        dispatch(changeStatus(id, { status: s ? 1 : 0 }));
    }

    const getBackgroundStyle = (status) => {
        return status === 0 ? { background: '#f4f4f4' } : {};
    };

    const serial = useMemo(() => {
        return index + 1 + (current_page - 1) * pageLength;
    }, [current_page, pageLength, index]);

    return (
        <tr>
            <td style={getBackgroundStyle(status)}>
                <div className="row_check">
                    <span>{serial}</span>
                    {checkPermission('Delete Facilities') &&
                        <label className="custom-checkbox checkbox">
                            <input type="checkbox" name="select-all" data-value={facility.id} checked={selectedItems.includes(facility.id.toString())} onChange={handleChange} />
                            <span className="checkmark"></span>
                        </label>
                    }
                </div>
            </td>
            <td style={getBackgroundStyle(status)}>
                {facility.name || facility.facility_name || facility.abbreviation}
            </td>
            <td style={getBackgroundStyle(status)}>
                {convertTimeToUTC(facility.created_at)}
            </td>
            {checkPermission('Status Facilities') &&
                <td style={getBackgroundStyle(status)}>
                    <CustomSwitch handleChange={(status) => handleStatusChange(facility?.id, status)} checked={status == 1 ? true : false} />
                </td>
            }
            {checkPermission('Edit Facilities') &&
                <td style={getBackgroundStyle(status)}>
                    <div className="row_check">
                        <button className="table_edit" data-bs-toggle="modal" data-bs-target="#exampleModal" onClick={() => handleAddPopUp({ ...facility, status })}>Edit</button>
                    </div>
                </td>
            }
        </tr>
    )
}

export default FacilityList;
